import type { Container } from 'pixi.js';
import {
  clearEndRaceEvent,
  getPlayerRank,
  isRaceEventJoinExpired,
  tryToActivateRaceEvent,
  updateRaceEvent,
} from '../RaceEventUtils';
import { openRaceEventPopup } from './openRaceEventPopup';
import type { RaceEventIconView } from '../views/RaceEventIconView';
import { showIconRankChange } from './showIconRankChange';
import type { RaceEventDependencies } from '../RaceEventController';

async function iconRankChangedFlow(config: RaceEventMenuFlowConfig): Promise<void> {
  const {
    dependencies: { observable, popupsController },
    raceEventIcon,
  } = config;

  const state = observable.getValue().raceEvent;
  if (!state) return;

  const { lastSeenIconPosition = 5 } = state;
  const currentPosition = getPlayerRank(state);

  if (!lastSeenIconPosition || lastSeenIconPosition === currentPosition || state.currentProgress === 0) {
    if (state.currentProgress !== 0) raceEventIcon.displayFlag(currentPosition);
  } else {
    await showIconRankChange({
      popups: popupsController,
      iconView: raceEventIcon,
      previousRank: lastSeenIconPosition,
      currentRank: currentPosition,
    });
  }

  observable.updateValue({
    raceEvent: {
      ...state,
      lastSeenIconPosition: currentPosition,
    }
  });
}

async function inEventFlow(config: RaceEventMenuFlowConfig): Promise<void> {
  const { dependencies, coinTarget, boosterTarget } = config;
  const { observable } = dependencies;

  updateRaceEvent(dependencies);
  const state = observable.getValue().raceEvent;
  if (!state) return;

  if (state.startDate === null) return;

  if (!state.finished) {
    await iconRankChangedFlow(config);
    return;
  }

  await openRaceEventPopup(dependencies, coinTarget, boosterTarget);
  clearEndRaceEvent(observable);
}

async function noEventFlow({ dependencies, coinTarget, boosterTarget }: RaceEventMenuFlowConfig): Promise<void> {
  const { observable, tracking } = dependencies;

  tryToActivateRaceEvent(dependencies);

  if (!observable.getValue().raceEvent) return;

  tracking.onFirstInstancePopupOpened();
  await openRaceEventPopup(dependencies, coinTarget, boosterTarget);
}

interface RaceEventMenuFlowConfig {
  dependencies: RaceEventDependencies;
  raceEventIcon: RaceEventIconView;
  coinTarget: Container;
  boosterTarget: Container;
}

export async function startMainMenuRaceEventFlow(config: RaceEventMenuFlowConfig): Promise<void> {
  const {
    dependencies: { observable },
  } = config;

  let raceEventState = observable.getValue().raceEvent;

  if (raceEventState && isRaceEventJoinExpired(raceEventState)) {
    clearEndRaceEvent(observable);
    raceEventState = null;
  }

  if (!raceEventState) {
    await noEventFlow(config);
  } else {
    await inEventFlow(config);
  }
}
